import { useCallback, useEffect, type ComponentProps } from "react";
import { bindScopeLocation } from "@/lib/api-client/estate-selection";
import {
  Link as RouterLink,
  Navigate,
  useNavigate,
  useRouter,
  useRouterState,
  useSearch,
} from "@tanstack/react-router";

declare module "@tanstack/react-router" {
  interface HistoryState {
    authEmail?: string;
  }
}

/**
 * The only module in the client that imports the router.
 *
 * Everything else goes through these, so swapping routers is a change to this
 * file rather than to every leaf that reads a param. The shapes are
 * deliberately the smaller, router-neutral ones — a plain URLSearchParams
 * rather than a tuple, a string destination rather than a typed route object.
 *
 * Destinations stay strings because they are built at runtime by
 * lib/navigation-routes' routeBuilders from ids the router never sees. Typed
 * `to` would be a lie here; it is worth having on the leaf routes later, not
 * on this seam.
 */

// Read off the deepest match rather than useParams: callers here are leaf
// pages that want whatever the URL carried, and useParams' typed form wants a
// route id this seam deliberately does not know.
export function useRouteParams<
  T extends Record<string, string | undefined>,
>(): T {
  // The cast sits outside select: TanStack's structural sharing needs a
  // concrete selected type and rejects an arbitrary generic.
  const params = useRouterState({
    select: (state) => state.matches.at(-1)?.params,
  });
  return (params ?? {}) as T;
}

export function useScopeRequestLocation() {
  const router = useRouter();
  const read = useCallback(
    () => ({
      pathname: router.state.location.pathname,
      search: router.state.location.searchStr,
    }),
    [router],
  );
  // Register before children run effects that issue their scoped requests.
  bindScopeLocation(read);
  useEffect(() => bindScopeLocation(read), [read]);
}

export function useRouteLocation() {
  return useRouterState({
    select: (state) => ({
      pathname: state.location.pathname,
      search: state.location.searchStr,
      state: state.location.state,
    }),
  });
}

/**
 * Reads where the router is now, at the moment it is called. A rendered
 * location can be a navigation behind: a handler that moves the address and
 * then navigates again has to build the second address from the first.
 */
export function useLatestRouteLocation() {
  const router = useRouter();
  return useCallback(() => {
    const { pathname, search } = router.history.location;
    return { pathname, search };
  }, [router]);
}

export function useRouteNavigate() {
  const navigate = useNavigate();
  return useCallback(
    (to: string, options?: { replace?: boolean; viewTransition?: boolean }) => {
      // The router takes a path and a search object separately; a query
      // string left inside `to` reaches the address bar but never the match
      // that useRouteSearch reads.
      const [path, query = ""] = to.split("?", 2);
      const search = query
        ? Object.fromEntries(new URLSearchParams(query))
        : undefined;
      return navigate({
        to: path,
        search,
        replace: options?.replace,
        viewTransition: options?.viewTransition,
      });
    },
    [navigate],
  );
}

/**
 * Read-only view of the query string, taken from the component's own match
 * rather than the router's current location.
 *
 * The distinction matters while a navigation is in flight: a page that reads
 * the global location re-renders mid-transition with the *destination's*
 * search, which for a page that redirects on one branch means the fallback
 * branch runs and issues a second, competing redirect.
 */
export function useRouteSearch(): URLSearchParams {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  return toSearchParams(search);
}

/**
 * Query-string writes. Returned separately from the read so a component that
 * only reads the address cannot change it by accident.
 *
 * An updater is applied to the address as it stands when the write lands, not
 * as it stood at render: two writes in one handler then both survive.
 */
export function useSetRouteSearch() {
  const navigate = useNavigate();
  return useCallback(
    (
      next: URLSearchParams | ((current: URLSearchParams) => URLSearchParams),
      options?: { replace?: boolean; viewTransition?: boolean },
    ) => {
      const search = (current: Record<string, unknown>) =>
        Object.fromEntries(
          (typeof next === "function"
            ? next(toSearchParams(current))
            : next
          ).entries(),
        );
      // A replace refines the view the reader is on, so it keeps their scroll;
      // the router would otherwise reset the window to the top on every write.
      const replace = options?.replace ?? false;
      void navigate({
        to: ".",
        search,
        replace,
        resetScroll: !replace,
        viewTransition: options?.viewTransition,
      });
    },
    [navigate],
  );
}

function toSearchParams(search: Record<string, unknown> | undefined) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search ?? {})) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  return params;
}

/**
 * Writes some keys of the query and keeps the rest, in place of the current
 * history entry. Merged into the router's latest location rather than one a
 * component rendered, so a page that moved its own query in the same commit
 * keeps what it wrote.
 */
export function useMergeRouteSearch() {
  const navigate = useNavigate();
  return useCallback(
    (entries: Record<string, string>) => {
      void navigate({
        to: ".",
        search: (previous: Record<string, unknown>) => ({
          ...previous,
          ...entries,
        }),
        replace: true,
      });
    },
    [navigate],
  );
}
export function AppLink({
  to,
  ...props
}: Omit<ComponentProps<"a">, "href"> & {
  to: string;
  state?: { authEmail?: string };
}) {
  return <RouterLink to={to} {...props} />;
}

/**
 * Declarative redirect, rendered in place of a page. The router's own
 * component rather than a navigate() call: calling navigate during render is
 * a side effect React is free to discard, and it silently did.
 */
export function RouteRedirect({
  to,
  replace = true,
}: {
  to: string;
  replace?: boolean;
}) {
  return <Navigate to={to} replace={replace} />;
}
